import { useEffect, useMemo, useState, KeyboardEvent, type JSX } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, FreeMode, Pagination, Swiper as SwiperType } from 'swiper';
import { SwiperModule } from 'swiper/types/shared';
import { PaginationOptions } from 'swiper/types/modules/pagination';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/free-mode';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import styles from './styles/styles.module.scss';
import { ArrowStyle, LeftArrow, RightArrow } from './ArrowButtons';

const PAGINATION_WRAPPER_CLASS = 'swiper-pagination';

type SwiperCarouselProps<T> = {
  uniqueId: string;
  items: T[];
  renderItem: (item: T, index: number) => JSX.Element;
  getItemKey: (item: T, index: number) => string;
  className?: string;
  loop?: boolean;
  showPaginationDots?: boolean;
  showOverflow?: boolean;
  hideSlideOverflow?: boolean;
  scrollMode?: boolean;
  gap?: number;
  hasPaginationGradient?: boolean;
  itemsPerGroup?: number | 'auto';
  itemsPerSlide?: number | 'auto';
  onFirstTouch?: () => void;
  onSlideChange?: (index: number) => void;
  onStartInteracting?: () => void;
  onStopInteracting?: () => void;
  arrowStyle?: ArrowStyle;
  isColdStart?: boolean;
  arrowClassName?: string;
  leftArrowClassName?: string;
  rightArrowClassName?: string;
  initialSlideIndex?: number;
  tabIndex?: number;
  preventActiveElementBlur?: boolean;
};

// This has been copied from the monorepo - the newer Carousel component is generally built for cards
// This component is generally used for swiping through images
const SwiperCarousel = <T,>({
  uniqueId,
  items,
  renderItem,
  getItemKey,
  className,
  loop,
  showPaginationDots,
  showOverflow,
  hideSlideOverflow,
  scrollMode,
  gap = 0,
  itemsPerGroup,
  itemsPerSlide = 1,
  onSlideChange,
  onFirstTouch,
  onStartInteracting,
  onStopInteracting,
  arrowStyle = 'standard',
  hasPaginationGradient,
  isColdStart,
  arrowClassName,
  leftArrowClassName,
  rightArrowClassName,
  initialSlideIndex = 0,
  tabIndex = 0,
  preventActiveElementBlur = false
}: SwiperCarouselProps<T>) => {
  const intl = useIntl();

  const [swiper, setSwiper] = useState<SwiperType>();
  const [isPrevDisabled, setIsPrevDisabled] = useState(false);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [onFirstTouchCalled, setOnFirstTouchCalled] = useState(false);

  const updateArrowStates = () => {
    if (scrollMode) {
      setIsPrevDisabled(true);
      setIsNextDisabled(true);
    } else if (loop) {
      setIsPrevDisabled(false);
      setIsNextDisabled(false);
    } else if (swiper) {
      setIsPrevDisabled(swiper.isBeginning);
      setIsNextDisabled(swiper.isEnd);
    } else {
      setIsPrevDisabled(false);
      setIsNextDisabled(false);
    }
  };

  useEffect(updateArrowStates, [loop, scrollMode, swiper]);

  useEffect(() => {
    if (!swiper) return () => {};

    const touchStartHandler = () => {
      if (!onFirstTouchCalled) {
        onFirstTouch?.();
        setOnFirstTouchCalled(true);
      }
    };

    if (onFirstTouch) {
      swiper.once('touchStart', touchStartHandler);
    }

    const slideChangeHandler = () => {
      if (onFirstTouch && !onFirstTouchCalled) {
        // If the user is navigating with a keyboard, they will never trigger the true "first touch" action
        onFirstTouch();
        setOnFirstTouchCalled(true);
      }
      onSlideChange?.(swiper.realIndex);
    };

    const transitionEndHandler = () => {
      updateArrowStates();
    };

    swiper.on('slideChangeTransitionEnd', slideChangeHandler);
    swiper.on('transitionEnd', transitionEndHandler);

    return () => {
      swiper.off('slideChangeTransitionEnd', slideChangeHandler);
      swiper.off('transitionEnd', transitionEndHandler);
      if (onFirstTouch) {
        swiper.off('touchStart', touchStartHandler);
      }
    };
  }, [swiper, onFirstTouch, onFirstTouchCalled, onSlideChange, updateArrowStates]);

  useEffect(() => {
    if (!swiper || !onStartInteracting || !onStopInteracting) {
      return;
    }

    swiper.on('transitionStart', onStartInteracting);
    swiper.on('sliderFirstMove', onStartInteracting);
    swiper.on('touchMove', onStartInteracting);

    swiper.on('transitionEnd', onStopInteracting);
    swiper.on('touchEnd', onStopInteracting);

    // eslint-disable-next-line consistent-return
    return () => {
      swiper.off('transitionStart', onStartInteracting);
      swiper.off('sliderFirstMove', onStartInteracting);
      swiper.off('touchMove', onStartInteracting);
      swiper.off('transitionEnd', onStopInteracting);
      swiper.off('touchEnd', onStopInteracting);
    };
  }, [onStartInteracting, onStopInteracting, swiper]);
  const prevSlide = () => {
    swiper?.slidePrev();
  };

  const nextSlide = () => {
    swiper?.slideNext();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowLeft':
        prevSlide();
        break;
      case 'ArrowRight':
        nextSlide();
        break;
      default:
      // do nothing
    }
  };

  const modules = [A11y, Pagination, scrollMode && FreeMode].filter(module => !!module) as SwiperModule[];

  const slidesPerGroup = useMemo(() => {
    if (itemsPerGroup) {
      return itemsPerGroup === 'auto' ? undefined : itemsPerGroup;
    }
    return itemsPerSlide === 'auto' ? undefined : itemsPerSlide;
  }, [itemsPerGroup, itemsPerSlide]);

  const paginationOptions: PaginationOptions | undefined = useMemo(() => {
    if (showPaginationDots) {
      return {
        clickable: true,
        dynamicBullets: true,
        dynamicMainBullets: 3,
        el: `.${PAGINATION_WRAPPER_CLASS}`,
        bulletElement: 'button'
      };
    }
    return undefined;
  }, [showPaginationDots]);

  return (
    <div className={styles.container}>
      <LeftArrow
        className={classNames(arrowClassName, leftArrowClassName)}
        onClick={prevSlide}
        uniqueId={uniqueId}
        title={intl.formatMessage({
          defaultMessage: 'Navigate previous'
        })}
        ariaLabel={intl.formatMessage({
          defaultMessage: 'Previous slide'
        })}
        variant={arrowStyle}
        disabled={isPrevDisabled}
      />
      <RightArrow
        className={classNames(arrowClassName, rightArrowClassName)}
        onClick={nextSlide}
        uniqueId={uniqueId}
        title={intl.formatMessage({ defaultMessage: 'Navigate next' })}
        ariaLabel={intl.formatMessage({
          defaultMessage: 'Next slide'
        })}
        variant={arrowStyle}
        disabled={isNextDisabled}
      />

      <div
        className={classNames(isColdStart ? styles.coldStartSwiperContainer : styles.swiperContainer, showOverflow && styles.showOverflow, className)}
        tabIndex={tabIndex}
        onKeyDown={onKeyDown}
        role="button"
        aria-label={intl.formatMessage({ defaultMessage: 'Carousel' })}
      >
        <Swiper
          className={classNames(
            isColdStart ? styles.coldStartSwiper : styles.swiper,
            hideSlideOverflow && styles.hideSlideOverflow,
            itemsPerSlide === 'auto' && styles.autoSize
          )}
          onSwiper={setSwiper}
          modules={modules.length ? modules : undefined}
          pagination={paginationOptions}
          a11y={{
            enabled: true,
            prevSlideMessage: intl.formatMessage({ defaultMessage: 'Previous slide' }),
            nextSlideMessage: intl.formatMessage({ defaultMessage: 'Next slide' }),
            paginationBulletMessage: intl.formatMessage({ defaultMessage: 'Go to slide {index}' }, { index: '{{index}}' }),
            slideLabelMessage: '{{index}} / {{slidesLength}}',
            slideRole: 'group',
            notificationClass: 'swiper-notification'
          }}
          loop={loop}
          spaceBetween={gap}
          freeMode={scrollMode}
          slidesPerView={itemsPerSlide}
          slidesPerGroup={slidesPerGroup}
          slidesPerGroupAuto={itemsPerGroup === 'auto'}
          initialSlide={initialSlideIndex}
          focusableElements={preventActiveElementBlur ? 'video video video' : undefined} // Need a valid css selector that targets no elements
        >
          {items.map((item, index) => (
            <SwiperSlide key={getItemKey(item, index)}>{renderItem(item, index)}</SwiperSlide>
          ))}

          {showPaginationDots && (
            <div
              className={classNames(styles.paginationWrapper, {
                [styles.paginationGradient]: hasPaginationGradient
              })}
            >
              <div className={PAGINATION_WRAPPER_CLASS} />
            </div>
          )}
        </Swiper>
      </div>
    </div>
  );
};

export default SwiperCarousel;
